import moment from 'moment';
import { CMIC_TREE_NODE_TYPES, SPLIT_SIZES } from '../../../shared/Constants';
import { saveAttachment } from '../../tasks/SaveAttachment';
import { createPMDocumentRecord, uploadDocumentsToRecord } from '../../tasks/upload/CreateDocumentRecord';
import { DEAFULT_DOC_FIELDS, FIELD_TYPES } from './constants';
import { createDocRevisionRecord, sendCustomFields, uploadRevisionDocToRecord } from './services';

// @desc Returns whole array if there's any required field
export const checkReqCustomFields = arr => {
	const requiredFields = arr.filter(ele => ele.ActiveFlag === 'Y').sort((a, b) => a.SortOrder < b.SortOrder);
	for (let ele of requiredFields) {
		if (ele.RequiredFlag === 'Y') return requiredFields;
	}
	return [];
};

// @desc Check if it's a default field or not
const checkDefaultField = fieldCode => {
	// Checking from whole list if field's code is present
	for (let code in DEAFULT_DOC_FIELDS) {
		if (DEAFULT_DOC_FIELDS[code] === fieldCode) return true;
	}

	return false;
};

// @desc: Gathers metadata for email attachments
export const gatherAttchData = async attachments => {
	const docsData = [];

	for (let att of attachments) {
		try {
			const response = await saveAttachment(att);
			docsData.push({ ...att, ...response });
		} catch (error) {
			console.log('gatherAttchData() Error', error);
		}
	}

	return docsData;
};

// @desc Gather fields for attachments upload
export const gatherAttachmentFields = (fields, isDocIdPresent) => {
	// Adding Status for doc as a deafault field
	// Along with Doc Id on conditional basis
	const formattedFields = appendDefaultFields(isDocIdPresent);

	for (let field of fields) {
		const name = `${field.FieldDesc}-${field.SortOrder}`;
		const isDropDown = field?.LovCode && field?.FieldCode;
		formattedFields[name] = {
			...field,
			value: field?.DefaultValue || '',
			isValid: field?.RequiredFlag === 'Y' ? field?.DefaultValue || false : true,
			type: isDropDown ? FIELD_TYPES.DROPDOWN : FIELD_TYPES.INPUT,
			isRequired: field?.RequiredFlag === 'Y',
			name,
		};
	}

	return formattedFields;
};

// @desc Gather fields for revisions upload
export const gatherRevisionsFields = () => {
	const formattedFields = {
		[DEAFULT_DOC_FIELDS.DESCRIPTION]: {
			isValid: true,
			type: FIELD_TYPES.INPUT,
			value: '',
			name: DEAFULT_DOC_FIELDS.DESCRIPTION,
			FieldDesc: 'Description',
			FieldCode: DEAFULT_DOC_FIELDS.DESCRIPTION,
			FieldNumber: 0,
			SortOrder: 0,
			RequiredFlag: 'N',
		},
		[DEAFULT_DOC_FIELDS.REV_DATE]: {
			isValid: true,
			type: FIELD_TYPES.DATE,
			value: moment(new Date()).format('MMM-DD-YYYY'),
			name: DEAFULT_DOC_FIELDS.REV_DATE,
			FieldDesc: 'Date',
			FieldCode: DEAFULT_DOC_FIELDS.REV_DATE,
			FieldNumber: 0,
			SortOrder: 0,
			isDisabled: true,
			RequiredFlag: 'N',
			iconName: 'Calendar',
		},
	};

	return formattedFields;
};

//@desc format default fields for attachment upload
const appendDefaultFields = isDocIdPresent => {
	const formattedFields = {};

	if (isDocIdPresent) {
		formattedFields[DEAFULT_DOC_FIELDS.DOC_ID] = {
			isValid: false,
			type: FIELD_TYPES.INPUT,
			value: '',
			name: DEAFULT_DOC_FIELDS.DOC_ID,
			FieldDesc: 'Document ID',
			FieldCode: DEAFULT_DOC_FIELDS.DOC_ID,
			FieldNumber: 0,
			SortOrder: 0,
			RequiredFlag: 'Y',
			isRequired: true,
		};
	}

	formattedFields[DEAFULT_DOC_FIELDS.STATUS] = {
		isValid: true,
		type: FIELD_TYPES.DROPDOWN,
		value: '',
		name: DEAFULT_DOC_FIELDS.STATUS,
		FieldDesc: 'Status',
		FieldCode: DEAFULT_DOC_FIELDS.STATUS,
		FieldNumber: 0,
		SortOrder: 0,
	};

	return formattedFields;
};

//@desc Exctract required fields only with values
export const extractRequiredFields = fields => {
	const defaultFields = {};
	const customFields = [];

	Object.values(fields).map(field => {
		if (checkDefaultField(field.FieldCode) && field.value) {
			defaultFields[field.FieldCode] = field.value?.data || field.value;
		} else if (field.value?.data || field.value) {
			customFields.push(formatCustomFields(field));
		}
	});

	return {
		defaultFields,
		customFields,
	};
};

export const handleSplitSize = (splitSize, activeNode) => {
	if (splitSize > SPLIT_SIZES.NO_ATTCH) {
		if (activeNode === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION) {
			if (splitSize === SPLIT_SIZES.ECM) return `87dvh`;
			else return `90dvh`;
		} else {
			if (splitSize === SPLIT_SIZES.ECM) return `74dvh`;
			else return `80dvh`;
		}
	} else {
		return '90dvh';
	}
};

export const handleAttachmentVisiblity = nodeType => {
	switch (nodeType) {
		case CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE:
			return {
				isAttachmentDocVisible: true,
				size: SPLIT_SIZES.ECM,
			};

		case CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION:
			return {
				isAttachmentDocVisible: true,
				size: SPLIT_SIZES.ECM,
			};

		default:
			return {
				isAttachmentDocVisible: false,
				size: SPLIT_SIZES.NO_ATTCH,
			};
	}
};

export const handleDisableColumnMenu = nodeType => {
	switch (nodeType) {
		case CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION:
			return true;

		default:
			return false;
	}
};

export const uploadAttachments = async (attachments, userInfo, selectedProject, uploadData, customFields = []) => {
	const failedAttachments = [];
	for (const mailData of attachments) {
		try {
			// Creating Record to upload document
			const recordData = await createPMDocumentRecord(uploadData, mailData, userInfo, selectedProject);
			if (!recordData) throw Error('Attachment failed!');

			// Uploading record
			const uploadResponse = await uploadDocumentsToRecord(recordData, mailData, userInfo);

			if (!uploadResponse) throw new Error('Upload Failed!');

			if (customFields.length > 0) await sendCustomFields(userInfo, recordData, customFields);
		} catch (error) {
			console.log('uploadAttachments() Error', error);
			failedAttachments.push(mailData);
		}
	}

	return { failedAttachments };
};

export const uploadRevisions = async (attachments, userInfo, uploadData) => {
	const failedAttachments = [];
	let revNum = uploadData.PmdRevNum ? Number(uploadData.PmdRevNum) : 0;

	//Adding to send new and updated revision number
	revNum++;

	for (const mailData of attachments) {
		try {
			// Creating Record to upload document revision
			const recordData = await createDocRevisionRecord({ ...uploadData, name: mailData.name, revNum }, userInfo);

			if (!recordData) throw Error('Attachment failed!');

			// Uploading revision doc to record
			const uploadResponse = await uploadRevisionDocToRecord(recordData.VUuid, mailData, userInfo);

			if (!uploadResponse) throw new Error('Upload Failed!');

			revNum++;
		} catch (error) {
			console.log('uploadAttachments() Error', error);
			failedAttachments.push(mailData);
		}
	}

	return { failedAttachments };
};

const formatCustomFields = field => {
	const newField = { ...field };
	delete newField.isValid;
	delete newField.type;
	delete newField.isRequired;
	delete newField.name;
	delete newField.ObjectSubType;
	delete newField.ObjectType;

	const Value = newField.value?.data || newField.value;
	delete newField.value;
	return {
		Definition: {
			...newField,
		},
		Value,
	};
};
